// ABOUTME: CSM Usage Analytics dashboard component for identifying family performance patterns and engagement trends
// ABOUTME: Displays performance metrics, family status, and engagement trends for customer support management

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Container,
  Button,
  Paper,
  ListItemAvatar,
  Avatar,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Refresh,
  TrendingUp,
  People,
  Warning,
  Error as ErrorIcon,
  CheckCircle,
  ArrowBack,
  Timeline,
  Launch
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { useTheme, alpha } from '@mui/material/styles';
import { getFamilyUsageAnalytics, getDailyEngagementTrends } from '../services/api';
import { useTimezone } from '../contexts/TimezoneContext';
import { chartAccentBlue } from '../theme/tokens';

interface PerformanceMetrics {
  totalFamilies: number;
  familiesMeetingTarget: number;
  familiesAtRisk: number;
  familiesCritical: number;
}

interface PriorityFamily {
  familyId: number;
  familyName: string;
  riskLevel: 'Performing' | 'AtRisk' | 'Critical' | number;
  lastBehaviorLogged: string | null;
  avgBehaviorsPerChildPerDay: number;
  childrenCount: number;
  daysWithoutLogging: number;
}

interface TrendData {
  totalLogins: number;
  avgBehaviorsPerFamily: number;
}

interface EngagementTrends {
  period7Days: TrendData;
  period30Days: TrendData;
  period90Days: TrendData;
}

interface DailyEngagement {
  date: string;
  activeFamilies: number;
  totalBehaviors: number;
  avgBehaviorsPerFamily: number;
  familiesMeetingTarget: number;
  engagementRate: number;
  churnRisk: number;
}

interface UsageAnalytics {
  performanceMetrics: PerformanceMetrics;
  priorityFamilies: PriorityFamily[];
  engagementTrends: EngagementTrends;
}

const AdminUsageAnalytics: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { getTodayInFamilyTimezone } = useTimezone();
  const [analytics, setAnalytics] = useState<UsageAnalytics | null>(null);
  const [dailyTrends, setDailyTrends] = useState<DailyEngagement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timePeriod, setTimePeriod] = useState<number>(30);

  const fetchAnalytics = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      
      // Fetch both analytics and daily trends in parallel
      const [analyticsData, trendsData] = await Promise.all([
        getFamilyUsageAnalytics(timePeriod),
        getDailyEngagementTrends(timePeriod)
      ]);
      
      setAnalytics(analyticsData as UsageAnalytics);
      setDailyTrends(trendsData as DailyEngagement[]);
    } catch (err) {
      console.error('Failed to fetch usage analytics:', err);
      setError('Failed to load usage analytics');
    } finally {
      setLoading(false);
    }
  }, [timePeriod]);

  useEffect(() => {
    fetchAnalytics();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchAnalytics, 30000);
    
    return () => clearInterval(interval);
  }, [fetchAnalytics]);


  const getRiskChipLabel = (riskLevel: string | number) => {
    const level = String(riskLevel).toLowerCase();
    switch (level) {
      case 'critical':
      case '2': return 'Critical';
      case 'at-risk':
      case 'atrisk':
      case '1': return 'At Risk';
      case 'performing':
      case '0': return 'Performing';
      default: return String(riskLevel);
    }
  };

  const handleNavigateToFamily = (familyId: number) => {
    navigate(`/admin/families/${familyId}`);
  };

  const handleNavigateBack = () => {
    navigate('/admin');
  };

  const handleViewCriticalFamilies = () => {
    // Filter to show only critical families in the admin family management
    navigate('/admin/families?filter=critical');
  };

  const handleExportAtRiskList = () => {
    if (!analytics) return;
    
    const atRiskFamilies = analytics.priorityFamilies.filter(f => {
      const level = String(f.riskLevel || '').toLowerCase();
      return level === 'atrisk' || level === 'at-risk' || level === '1' || level === 'critical' || level === '2';
    });
    const csvContent = [
      'Family Name,Risk Level,Days Without Logging,Avg Behaviors Per Child Per Day,Children Count',
      ...atRiskFamilies.map(f => 
        `${f.familyName},${getRiskChipLabel(f.riskLevel)},${f.daysWithoutLogging},${f.avgBehaviorsPerChildPerDay},${f.childrenCount}`
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `at-risk-families-${getTodayInFamilyTimezone()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleGenerateReport = () => {
    if (!analytics) return;
    
    // Generate comprehensive HTML report
    const criticalFamilies = analytics.priorityFamilies.filter(f => {
      const level = String(f.riskLevel || '').toLowerCase();
      return level === 'critical' || level === '2';
    });
    const atRiskFamilies = analytics.priorityFamilies.filter(f => {
      const level = String(f.riskLevel || '').toLowerCase();
      return level === 'atrisk' || level === 'at-risk' || level === '1';
    });
    
    const reportHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>CSM Analytics Report - ${new Date().toLocaleDateString()}</title>
        <style>
          body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; }
          h1, h2 { color: ${theme.palette.info.main}; }
          .metric { background: ${theme.palette.grey[100]}; padding: 15px; margin: 10px 0; border-radius: 8px; }
          .critical { border-left: 4px solid ${theme.palette.error.dark}; }
          .warning { border-left: 4px solid ${theme.palette.warning.main}; }
          .success { border-left: 4px solid ${theme.palette.success.main}; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { padding: 10px; text-align: left; border-bottom: 1px solid ${theme.palette.divider}; }
          th { background: ${theme.palette.info.main}; color: white; }
          .recommendations { background: ${alpha(theme.palette.info.main, 0.1)}; padding: 20px; border-radius: 8px; margin: 20px 0; }
        </style>
      </head>
      <body>
        <h1>Customer Success Analytics Report</h1>
        <p>Generated on: ${new Date().toLocaleString()}</p>
        <p>Reporting Period: Last ${timePeriod} days</p>
        
        <h2>Executive Summary</h2>
        <div class="metric ${successRate >= 80 ? 'success' : successRate >= 50 ? 'warning' : 'critical'}">
          <strong>Success Rate:</strong> ${successRate}% of families meeting engagement targets
        </div>
        <div class="metric">
          <strong>Total Families:</strong> ${analytics.performanceMetrics.totalFamilies}<br>
          <strong>Meeting Target (15+ behaviors/child/day):</strong> ${analytics.performanceMetrics.familiesMeetingTarget}<br>
          <strong>At Risk:</strong> ${analytics.performanceMetrics.familiesAtRisk}<br>
          <strong>Critical:</strong> ${analytics.performanceMetrics.familiesCritical}
        </div>
        
        <h2>Revenue Impact</h2>
        <div class="metric">
          <strong>Potential Monthly Revenue at Risk:</strong> $${(criticalFamilies.length * 10).toFixed(2)}<br>
          <strong>Potential Annual Revenue at Risk:</strong> $${(criticalFamilies.length * 100).toFixed(2)}
        </div>
        
        <h2>Critical Families Requiring Immediate Action</h2>
        ${criticalFamilies.length > 0 ? `
          <table>
            <tr>
              <th>Family Name</th>
              <th>Days Without Logging</th>
              <th>Avg Behaviors/Child/Day</th>
              <th>Children</th>
              <th>Risk Score</th>
            </tr>
            ${criticalFamilies.map(f => `
              <tr>
                <td>${f.familyName}</td>
                <td>${f.daysWithoutLogging}</td>
                <td>${f.avgBehaviorsPerChildPerDay}</td>
                <td>${f.childrenCount}</td>
                <td style="color: ${theme.palette.error.dark};">Critical</td>
              </tr>
            `).join('')}
          </table>
        ` : '<p>No critical families - excellent retention!</p>'}
        
        <h2>At-Risk Families</h2>
        ${atRiskFamilies.length > 0 ? `
          <p>${atRiskFamilies.length} families showing declining engagement. Early intervention recommended.</p>
        ` : '<p>No at-risk families identified.</p>'}
        
        <div class="recommendations">
          <h2>Recommended Actions</h2>
          <ul>
            ${criticalFamilies.length > 0 ? '<li><strong>Immediate:</strong> Personal outreach to all critical families within 24 hours</li>' : ''}
            ${atRiskFamilies.length > 0 ? '<li><strong>This Week:</strong> Send engagement tips email to at-risk families</li>' : ''}
            <li><strong>Ongoing:</strong> Monitor families approaching the 5 behaviors/child/day threshold</li>
            <li><strong>Success Stories:</strong> Share best practices from high-performing families</li>
          </ul>
        </div>
        
        <h2>Engagement Trends</h2>
        <p>Average daily behaviors per family over the period: ${(dailyTrends.reduce((sum, d) => sum + d.avgBehaviorsPerFamily, 0) / dailyTrends.length).toFixed(1)}</p>
        
        <p><em>This report is confidential and intended for internal use only.</em></p>
      </body>
      </html>
    `;
    
    const blob = new Blob([reportHtml], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `csm-analytics-report-${getTodayInFamilyTimezone()}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Convert daily trends data for charts
  const formatTrendData = () => {
    return dailyTrends.map(day => ({
      date: new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      fullDate: day.date,
      engagement: day.engagementRate,
      activeFamilies: day.activeFamilies,
      avgBehaviors: day.avgBehaviorsPerFamily,
      churnRisk: day.churnRisk,
      totalBehaviors: day.totalBehaviors,
      familiesMeetingTarget: day.familiesMeetingTarget
    }));
  };

  // Colors for charts
  const COLORS = {
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    primary: theme.palette.info.main,
    engagement: chartAccentBlue,
    churn: theme.palette.destructiveAccent
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }} data-testid="usage-analytics-loading">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!analytics) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        No analytics data available
      </Alert>
    );
  }

  const successRate = analytics.performanceMetrics.totalFamilies > 0 
    ? Math.round((analytics.performanceMetrics.familiesMeetingTarget / analytics.performanceMetrics.totalFamilies) * 100)
    : 0;

  const trendData = formatTrendData();

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header with Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={handleNavigateBack}
          >
            Back to Admin
          </Button>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            CSM Usage Analytics Dashboard
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={timePeriod}
              label="Time Period"
              onChange={(e) => setTimePeriod(Number(e.target.value))}
            >
              <MenuItem value={7}>7 Days</MenuItem>
              <MenuItem value={30}>30 Days</MenuItem>
              <MenuItem value={90}>90 Days</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={fetchAnalytics}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* CSM Insights Banner */}
      <Paper sx={{ p: 3, mb: 4, bgcolor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Timeline sx={{ fontSize: 32 }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Customer Success Insights
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          Monitor family engagement patterns to identify at-risk accounts and optimize retention. 
          Families averaging 15+ behaviors per child daily show 85% higher subscription renewal rates.
        </Typography>
      </Paper>

      {/* Performance Metrics with Family Lists */}
      <Box 
        sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, 
          gap: 3, 
          mb: 4 
        }}
      >
        {/* Total Families Overview */}
        <Box>
          <Card>
            <CardContent sx={{ textAlign: 'center', pb: 2 }}>
              <People sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" component="div" sx={{ fontWeight: 600 }}>
                {analytics.performanceMetrics.totalFamilies}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Families
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Meeting Target */}
        <Box>
          <Card sx={{ height: 400, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ textAlign: 'center', pb: 1 }}>
              <CheckCircle sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
              <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                {analytics.performanceMetrics.familiesMeetingTarget}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Meeting Target
              </Typography>
            </CardContent>
            <Divider />
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <Typography variant="subtitle2" sx={{ p: 2, pb: 1, fontWeight: 600 }}>
                Top Performers
              </Typography>
              <List sx={{ pt: 0, maxHeight: 280, overflow: 'auto' }}>
                {analytics.priorityFamilies
                  .filter(f => {
                    const level = String(f.riskLevel || '').toLowerCase();
                    return level === 'performing' || level === '0';
                  })
                  .slice(0, 10)
                  .map((family) => (
                    <ListItem 
                      key={family.familyId} 
                      onClick={() => handleNavigateToFamily(family.familyId)}
                      sx={{ py: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'success.main', color: 'success.contrastText', width: 32, height: 32 }}>
                          <CheckCircle sx={{ fontSize: 18 }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={family.familyName}
                        secondary={`${family.avgBehaviorsPerChildPerDay.toFixed(1)} avg/child/day`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <ListItemSecondaryAction>
                        <Launch sx={{ fontSize: 16, color: 'action.active' }} />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                {analytics.priorityFamilies.filter(f => {
                  const level = String(f.riskLevel || '').toLowerCase();
                  return level === 'performing' || level === '0';
                }).length === 0 && (
                  <ListItem>
                    <ListItemText 
                      primary="No families meeting target"
                      secondary="Focus on moving at-risk families up"
                      primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          </Card>
        </Box>

        {/* At Risk */}
        <Box>
          <Card sx={{ height: 400, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ textAlign: 'center', pb: 1 }}>
              <Warning sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
              <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                {analytics.performanceMetrics.familiesAtRisk}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                At Risk
              </Typography>
            </CardContent>
            <Divider />
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <Typography variant="subtitle2" sx={{ p: 2, pb: 1, fontWeight: 600 }}>
                Needs Attention
              </Typography>
              <List sx={{ pt: 0, maxHeight: 280, overflow: 'auto' }}>
                {analytics.priorityFamilies
                  .filter(f => {
                    const level = String(f.riskLevel || '').toLowerCase();
                    return level === 'atrisk' || level === 'at-risk' || level === '1';
                  })
                  .sort((a, b) => b.daysWithoutLogging - a.daysWithoutLogging)
                  .slice(0, 10)
                  .map((family) => (
                    <ListItem 
                      key={family.familyId} 
                      onClick={() => handleNavigateToFamily(family.familyId)}
                      sx={{ py: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'warning.main', color: 'warning.contrastText', width: 32, height: 32 }}>
                          <Warning sx={{ fontSize: 18 }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={family.familyName}
                        secondary={`${family.daysWithoutLogging} days without logging`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <ListItemSecondaryAction>
                        <Launch sx={{ fontSize: 16, color: 'action.active' }} />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                {analytics.priorityFamilies.filter(f => {
                  const level = String(f.riskLevel || '').toLowerCase();
                  return level === 'atrisk' || level === 'at-risk' || level === '1';
                }).length === 0 && (
                  <ListItem>
                    <ListItemText 
                      primary="No at-risk families"
                      secondary="Great engagement across all families!"
                      primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          </Card>
        </Box>

        {/* Critical */}
        <Box>
          <Card sx={{ height: 400, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ textAlign: 'center', pb: 1 }}>
              <ErrorIcon sx={{ fontSize: 32, color: 'error.main', mb: 1 }} />
              <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                {analytics.performanceMetrics.familiesCritical}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Critical
              </Typography>
            </CardContent>
            <Divider />
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <Typography variant="subtitle2" sx={{ p: 2, pb: 1, fontWeight: 600 }}>
                Immediate Action
              </Typography>
              <List sx={{ pt: 0, maxHeight: 280, overflow: 'auto' }}>
                {analytics.priorityFamilies
                  .filter(f => {
                    const level = String(f.riskLevel || '').toLowerCase();
                    return level === 'critical' || level === '2';
                  })
                  .sort((a, b) => b.daysWithoutLogging - a.daysWithoutLogging)
                  .slice(0, 10)
                  .map((family) => (
                    <ListItem 
                      key={family.familyId} 
                      onClick={() => handleNavigateToFamily(family.familyId)}
                      sx={{ py: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'error.main', color: 'error.contrastText', width: 32, height: 32 }}>
                          <ErrorIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={family.familyName}
                        secondary={`${family.daysWithoutLogging} days without logging`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <ListItemSecondaryAction>
                        <Launch sx={{ fontSize: 16, color: 'action.active' }} />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                {analytics.priorityFamilies.filter(f => {
                  const level = String(f.riskLevel || '').toLowerCase();
                  return level === 'critical' || level === '2';
                }).length === 0 && (
                  <ListItem>
                    <ListItemText 
                      primary="No critical families"
                      secondary="Excellent retention metrics!"
                      primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          </Card>
        </Box>
      </Box>

      {/* Success Rate and Key Metrics */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 3, mb: 4 }}>
        <Box>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUp sx={{ fontSize: 50, color: successRate >= 70 ? 'success.main' : 'warning.main', mb: 2 }} />
              <Typography variant="h3" component="div" sx={{ fontWeight: 600, mb: 1 }}>
                {successRate}%
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Success Rate
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Based on {timePeriod}-day rolling average
              </Typography>
            </CardContent>
          </Card>
        </Box>
        
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                30-Day Engagement Trends
              </Typography>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value, name) => [
                      `${value}${name === 'engagement' ? '%' : ''}`, 
                      name === 'engagement' ? 'Engagement Rate' : 
                      name === 'activeFamilies' ? 'Active Families' : 'Avg Behaviors'
                    ]}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="engagement" 
                    stroke={COLORS.engagement}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    name="engagement"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="activeFamilies" 
                    stroke={COLORS.success}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    name="activeFamilies"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* CSM Action Insights */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 3, mb: 4 }}>
        {/* Churn Risk Analysis */}
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Churn Risk Analysis
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={trendData.slice(-7)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value) => [`${value}%`, 'Churn Risk']}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Bar dataKey="churnRisk" fill={COLORS.error} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Families with 7+ days of inactivity have 45% higher churn probability
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Revenue Impact Metrics */}
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Revenue Impact Insights
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'success.main' }}>
                  ${(analytics.performanceMetrics.familiesMeetingTarget * 10).toFixed(0)}/mo
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Monthly revenue from engaged families
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  or ${(analytics.performanceMetrics.familiesMeetingTarget * 100).toFixed(0)}/year
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'error.main' }}>
                  ${(analytics.performanceMetrics.familiesCritical * 10).toFixed(0)}/mo
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  At-risk monthly revenue
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  or ${(analytics.performanceMetrics.familiesCritical * 100).toFixed(0)}/year at risk
                </Typography>
              </Box>

              <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  💡 CSM Action Plan
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  • Prioritize critical families for immediate outreach
                  <br />
                  • Schedule check-ins with at-risk families
                  <br />
                  • Share success stories with performing families
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Engagement Patterns */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {timePeriod}-Day Engagement Patterns
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Line 
                    type="monotone" 
                    dataKey="avgBehaviors" 
                    stroke={COLORS.primary}
                    strokeWidth={3}
                    dot={{ r: 6 }}
                    name="Avg Behaviors/Family"
                  />
                </LineChart>
              </ResponsiveContainer>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Weekend dips are normal - focus on maintaining weekday engagement
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Quick Actions
              </Typography>
              
              <List sx={{ py: 0 }}>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    startIcon={<ErrorIcon />}
                    color="error"
                    onClick={handleViewCriticalFamilies}
                  >
                    View Critical Families
                  </Button>
                </ListItem>
                
                <ListItem sx={{ px: 0, py: 1 }}>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    startIcon={<Warning />}
                    color="warning"
                    onClick={handleExportAtRiskList}
                  >
                    Export At-Risk List
                  </Button>
                </ListItem>
                
                <ListItem sx={{ px: 0, py: 1 }}>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    startIcon={<TrendingUp />}
                    color="primary"
                    onClick={handleGenerateReport}
                  >
                    Generate Report
                  </Button>
                </ListItem>
              </List>

              <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  🎯 This Week&apos;s Goal
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Reduce critical families from {analytics.performanceMetrics.familiesCritical} to {Math.max(0, analytics.performanceMetrics.familiesCritical - 2)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Container>
  );
};

export default AdminUsageAnalytics;